import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PencilIcon } from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import { truckingBillAPI } from '../../api/waybillAPI';

const PHP = (n) => new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2 }).format(n || 0);
const fmt = (d) => d ? new Date(d).toLocaleDateString('en-PH') : '—';
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-PH') : '—';

export default function DailyCollectionPage() {
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(today);
  const [data, setData] = useState({ bills: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const fetchCollection = async () => {
    setLoading(true);
    try {
      const res = await truckingBillAPI.getDailyCollection(selectedDate);
      setData(res.data?.data || { bills: [], total: 0 });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCollection(); }, []);

  const bills = data.bills || [];

  return (
    <Layout pageTitle="Daily Collection Report">
      <style>{`
        @media print { .no-print { display: none !important; } }
        @media screen { .print-only { display: none !important; } }
      `}</style>

      <div className="space-y-5">
        {/* Controls */}
        <div className="flex items-center gap-3 flex-wrap no-print">
          <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
          <button onClick={fetchCollection}
            className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700">
            Load
          </button>
          <button onClick={() => window.print()}
            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50">
            Print / Export
          </button>
        </div>

        {/* Summary card */}
        <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">Total Collections for {selectedDate}</p>
            <p className="text-2xl font-semibold text-teal-700">₱{PHP(data.total)}</p>
          </div>
          <div className="text-sm text-gray-400">{bills.length} payment(s)</div>
        </div>

        {/* Collection table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Bill No.', 'Waybill No.', 'Customer', 'Bill Date', 'Due Date', 'Amount Collected', 'Received Time', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>{Array.from({ length: 8 }).map((_, j) => (
                    <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-200 rounded animate-pulse" /></td>
                  ))}</tr>
                ))
              ) : bills.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-400">No collections for this date.</td>
                </tr>
              ) : bills.map(b => {
                const customerName =
                  b.WayBillId?.CustomerId?.CustomerName ||
                  b.WayBillId?.CustomerId?.name || '—';
                return (
                  <tr key={b._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{b.BillName}</td>
                    <td className="px-4 py-3 text-gray-600">{b.WayBillId?.SerialNo || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{customerName}</td>
                    <td className="px-4 py-3 text-gray-600">{fmt(b.BillDate)}</td>
                    <td className="px-4 py-3 text-gray-600">{fmt(b.BillDueDate)}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">₱{PHP(b.amountDue)}</td>
                    <td className="px-4 py-3 text-gray-600">{fmtTime(b.receivedDate)}</td>
                    <td className="px-4 py-3 no-print">
                      <button onClick={() => navigate(`/billing/ff-billing/${b._id}/edit`)}
                        className="p-1.5 rounded hover:bg-teal-50 text-teal-600" title="View">
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {!loading && bills.length > 0 && (
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan={5} className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Total</td>
                  <td className="px-4 py-3 font-bold text-gray-900">₱{PHP(data.total)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Print-only footer */}
        <div className="print-only" style={{ marginTop: 32, textAlign: 'center', fontSize: '10pt' }}>
          Prepared by: __________________ &nbsp;&nbsp; Date: __________________
        </div>
      </div>
    </Layout>
  );
}
